import { useAtom } from '@reatom/npm-react'
import * as DateF from 'date-fns'
import styled from 'styled-components'
import { useMemo } from 'use-memo-one'

import { Numeric, Separator } from '@blade/ui-kit'
import { CurrencyModel } from '@root/entities/currency'
import { CurrencyIcon } from '@root/entities/currency/ui'
import { KnownPlatform, useNetworkLinks } from '@root/entities/network'
import { KnownPlatformIcon } from '@root/entities/network/ui/known-platform/known-platform-icon'
import { WalletAddressTruncated } from '@root/entities/wallet/ui/common/wallet-address-truncated'
import { DATE_FORMAT } from '@root/shared'
import { PlatformUtil } from '@root/shared/core'
import {
  AmountFormatted,
  CURRENCY_AMOUNT_NATIVE_PRESET,
} from '@root/shared/core/format'
import { useTranslation } from '@root/shared/localization'
import { CopyButton } from '@root/shared/ui-kit'

import type { PairModel } from '@root/entities/pair'

interface Props {
  pair: PairModel
}

const CloseArea = ({ pair }: Props) => {
  const { t } = useTranslation()
  const nativeNetworkCurrency = CurrencyModel.nativeOf(pair.network)
  const [txCreator] = useAtom(pair.initTxCreator)

  const fundingAtFormatted = useMemo(() => {
    if (!txCreator?.funding?.at) {
      return undefined
    }
    return DateF.format(txCreator.funding.at, DATE_FORMAT.dd_MM_yy__HH_mm)
  }, [txCreator?.funding?.at])

  return (
    <Root>
      {/* {txCreator?.balanceInToken && balanceUsd ? (
        <Item>
          <Key>{t('common.balance')}</Key>
          <Value>
            <AmountFormatted amount={txCreator.balanceInToken} />
            (<AmountFormatted amount={balanceUsd} currency="$" />)
          </Value>
        </Item>
      ) : undefined} */}

      {txCreator?.balanceInToken ? (
        <Item>
          <Key>
            {pair.quote.ticker} {t('common.balance').toLowerCase()}
          </Key>
          <Value>
            <Currency>
              <AmountFormatted
                amount={txCreator.balanceInToken}
                roundingMode="trunc"
              />
              <CurrencyIcon currency={pair.quote} size={16} shape="circle" />
            </Currency>
          </Value>
        </Item>
      ) : undefined}

      {txCreator?.buyVolumeInUsd ? (
        <Item>
          <Key>{t('common.bought')}</Key>
          <Value>
            <ValueContainer>
              <BoughtText>
                <AmountFormatted
                  amount={txCreator.buyVolumeInUsd}
                  currency="$"
                />
              </BoughtText>
              {txCreator.buys ? (
                <>
                  <div>/</div>
                  <div>
                    <BoughtText>
                      <AmountFormatted amount={txCreator.buys} />
                      <span> {t('common.txs')}</span>
                    </BoughtText>
                  </div>
                </>
              ) : undefined}
            </ValueContainer>
          </Value>
        </Item>
      ) : undefined}
      {txCreator?.sellVolumeInUsd ? (
        <Item>
          <Key>{t('common.sold')}</Key>
          <Value>
            <ValueContainer>
              <SoldText>
                <AmountFormatted
                  amount={txCreator.sellVolumeInUsd}
                  currency="$"
                />
              </SoldText>
              {txCreator.sells ? (
                <>
                  <div>/</div>
                  <div>
                    <SoldText>
                      <AmountFormatted amount={txCreator.sells} />
                      <span> {t('common.txs')}</span>
                    </SoldText>
                  </div>
                </>
              ) : undefined}
            </ValueContainer>
          </Value>
        </Item>
      ) : undefined}
      {txCreator?.funding?.amount ? (
        <Item>
          <Key>{t('common.funded-amount')}</Key>
          <Value>
            <Currency>
              <Numeric
                value={txCreator.funding.amount}
                preset={CURRENCY_AMOUNT_NATIVE_PRESET}
              />
              <CurrencyIcon currency={nativeNetworkCurrency} size={16} />
            </Currency>
          </Value>
        </Item>
      ) : undefined}
      {fundingAtFormatted ? (
        <Item>
          <Key>{t('common.funded')}</Key>
          <Value>{fundingAtFormatted}</Value>
        </Item>
      ) : undefined}
    </Root>
  )
}

const ClickArea = ({ pair }: Props) => {
  const { t } = useTranslation()

  const [txCreator] = useAtom(pair.initTxCreator)
  const { buildContractLink } = useNetworkLinks()
  const devWalletLink = useMemo(
    () =>
      txCreator?.address ? buildContractLink(txCreator.address) : undefined,
    [txCreator?.address, buildContractLink],
  )
  const fundingWalletLink = useMemo(
    () =>
      txCreator?.funding?.by
        ? buildContractLink(txCreator.funding.by)
        : undefined,
    [txCreator?.funding?.by, buildContractLink],
  )

  const handleDevWalletClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation()
    e.preventDefault()
    if (devWalletLink) {
      PlatformUtil.openLink(devWalletLink, '_blank')
    }
  }
  const handleFundingWalletClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation()
    e.preventDefault()
    if (fundingWalletLink) {
      PlatformUtil.openLink(fundingWalletLink, '_blank')
    }
  }

  const knownPlatform = useMemo(() => {
    return txCreator?.funding?.by
      ? KnownPlatform.tryGetKnownPlatform(txCreator.funding.by)
      : undefined
  }, [txCreator?.funding?.by])

  const knownPlatformLabel = useMemo(() => {
    return knownPlatform ? KnownPlatform.labelOf(knownPlatform) : undefined
  }, [knownPlatform])

  const withWallets = txCreator?.funding?.by || txCreator?.address

  return (
    <>
      {withWallets ? (
        <Sep variant="solid" color="var(--backgroundA-3)" />
      ) : undefined}
      {txCreator?.funding?.by ? (
        <Item>
          <Key>{t('common.funding')}</Key>
          <ValueContainer>
            <KnownPlatformIcon size={14} address={txCreator.funding.by} />
            <ValueLink
              onClick={handleFundingWalletClick}
              href={fundingWalletLink}
            >
              {knownPlatformLabel ? (
                <span>{knownPlatformLabel} </span>
              ) : (
                <WalletAddressTruncated walletAddress={txCreator.funding.by} />
              )}
            </ValueLink>
            <CopyButton size={14} value={txCreator.funding.by} />
          </ValueContainer>
        </Item>
      ) : undefined}
      {txCreator?.address ? (
        <Item>
          <Key>{t('common.dev_wallet')}</Key>
          <ValueContainer>
            <ValueLink onClick={handleDevWalletClick} href={devWalletLink}>
              <WalletAddressTruncated walletAddress={txCreator.address} />
            </ValueLink>
            <CopyButton size={14} value={txCreator.address} />
          </ValueContainer>
        </Item>
      ) : undefined}
    </>
  )
}

export const ExploreDevHoldingsInfo = {
  CloseArea,
  ClickArea,
}

const Root = styled.ul`
  all: unset;
  display: flex;
  flex-direction: column;
  gap: 5px;

  color: var(--text-10);

  font-size: 12px;
  font-weight: 500;
  line-height: normal;
`

const Item = styled.li`
  display: flex;
  gap: 8px;
  justify-content: space-between;
  align-items: center;
  color: var(--text-11);
`
const Value = styled.div`
  color: var(--text-11);
`
const ValueLink = styled.a`
  color: var(--text-11);
  transition: color 0.15s linear;
  &:link,
  &:visited {
    color: var(--text-11);
    text-decoration: none;
  }
  &:hover {
    color: var(--text-12);
  }
`
const Key = styled.div`
  color: var(--text-10);
`

const Currency = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`

const ValueContainer = styled.div`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 4px;
`

const BoughtText = styled.span`
  color: var(--up-9);
  font-weight: 600;
`

const SoldText = styled.span`
  color: var(--down-9);
  font-weight: 600;
`

const Sep = styled(Separator)`
  margin-block: 8px;
`
